import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import { useAuth } from './AuthContext';

const NotificationsContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Notification types with their corresponding actions and pages
  const notificationTypes = {
    GUEST_ARRIVAL: {
      icon: 'person',
      color: 'primary',
      actionText: 'View Guest',
      navigateTo: '/guests'
    },
    GUARDIAN_VERIFICATION: {
      icon: 'security',
      color: 'warning',
      actionText: 'Review',
      navigateTo: '/parents/verify'
    },
    PICKUP_COMPLETED: {
      icon: 'check_circle',
      color: 'success',
      actionText: 'View Log',
      navigateTo: '/pickup-logs'
    },
    SYSTEM_ALERT: {
      icon: 'warning',
      color: 'error',
      actionText: 'Settings',
      navigateTo: '/settings'
    },
    NEW_TEACHER: {
      icon: 'person_add',
      color: 'info',
      actionText: 'View Teachers',
      navigateTo: '/teachers'
    },
    NEW_STUDENT: {
      icon: 'school',
      color: 'info',
      actionText: 'View Students',
      navigateTo: '/students'
    },
    GUARDIAN_APPROVED: {
      icon: 'check_circle',
      color: 'success',
      actionText: 'View Parents',
      navigateTo: '/parents'
    },
    GUARDIAN_DENIED: {
      icon: 'cancel',
      color: 'error',
      actionText: 'View Parents',
      navigateTo: '/parents'
    },
    TAG_ASSIGNMENT: {
      icon: 'local_offer',
      color: 'primary',
      actionText: 'View Tags',
      navigateTo: '/tagging-identifier'
    },
    LICENSE_PLATE_ISSUE: {
      icon: 'directions_car',
      color: 'warning',
      actionText: 'View Cars',
      navigateTo: '/cars'
    }
  };

  // Load notifications on component mount and user change
  useEffect(() => {
    if (user?.id) {
      loadNotifications();
      // Set up real-time listener
      const interval = setInterval(loadNotifications, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user]);

  // Calculate unread count whenever notifications change
  useEffect(() => {
    const unread = notifications.filter(n => !n.read).length;
    setUnreadCount(unread);
  }, [notifications]);

  const loadNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      // For now, using mock data. In real implementation, this would call the API
      const mockNotifications = [
        {
          id: '1',
          type: 'GUEST_ARRIVAL',
          title: 'New Guest Arrival',
          message: 'Mark Smith has arrived to pick up Jane Doe',
          timestamp: new Date(Date.now() - 300000), // 5 minutes ago
          read: false,
          data: {
            guestName: 'Mark Smith',
            studentName: 'Jane Doe',
            guestId: 'guest_123'
          }
        },
        {
          id: '2',
          type: 'GUARDIAN_VERIFICATION',
          title: 'Guardian Verification Required',
          message: 'New guardian verification request from Sarah Johnson',
          timestamp: new Date(Date.now() - 900000), // 15 minutes ago
          read: false,
          data: {
            guardianName: 'Sarah Johnson',
            guardianId: 'guardian_456'
          }
        },
        {
          id: '3',
          type: 'PICKUP_COMPLETED',
          title: 'Pickup Completed',
          message: 'Emma Davis was successfully picked up by her parent',
          timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
          read: true,
          data: {
            studentName: 'Emma Davis',
            guardianName: 'Mike Davis',
            pickupId: 'pickup_789'
          }
        },
        {
          id: '4',
          type: 'SYSTEM_ALERT',
          title: 'System Update',
          message: 'SafeLine system update scheduled for tonight at 11 PM',
          timestamp: new Date(Date.now() - 3600000), // 1 hour ago
          read: false,
          data: {
            updateTime: '11:00 PM',
            maintenanceWindow: '30 minutes'
          }
        },
        {
          id: '5',
          type: 'TAG_ASSIGNMENT',
          title: 'New Tag Assigned',
          message: 'Tag TAG005 has been assigned to Jennifer Wilson',
          timestamp: new Date(Date.now() - 7200000), // 2 hours ago
          read: true,
          data: {
            tagNumber: 'TAG005',
            parentName: 'Jennifer Wilson'
          }
        },
        {
          id: '6',
          type: 'GUARDIAN_APPROVED',
          title: 'Guardian Approved',
          message: 'Robert Chen has been approved as a guardian',
          timestamp: new Date(Date.now() - 10800000), // 3 hours ago
          read: true,
          data: {
            guardianName: 'Robert Chen',
            approvedBy: 'Admin User'
          }
        }
      ];

      setNotifications(mockNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      // Update local state immediately
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );

      // In real implementation, call API to mark as read
      // await apiService.notifications.markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Update local state immediately
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );

      // In real implementation, call API to mark all as read
      // await apiService.notifications.markAllAsRead(user.id);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      // Update local state immediately
      setNotifications(prev => 
        prev.filter(n => n.id !== notificationId)
      );

      // In real implementation, call API to delete
      // await apiService.notifications.delete(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false,
      ...notification
    };

    setNotifications(prev => [newNotification, ...prev]);
  };

  const getNotificationConfig = (type) => {
    return notificationTypes[type] || {
      icon: 'info',
      color: 'default',
      actionText: 'View',
      navigateTo: '/dashboard'
    };
  };

  const getRecentNotifications = (limit = 5) => {
    return notifications
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  };

  const getUnreadNotifications = () => {
    return notifications.filter(n => !n.read);
  };

  const value = {
    notifications,
    loading,
    unreadCount,
    notificationTypes,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addNotification,
    getNotificationConfig,
    getRecentNotifications,
    getUnreadNotifications
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}; 